import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { BreadcrumbSegment } from
  '../../app/shared/models/breadcrumb.model';

/* Re-exported for back-compat with existing importers; the canonical
   definition now lives in `shared/models/breadcrumb.model.ts`. */
export type { BreadcrumbSegment };

@Component({
  selector: 'app-breadcrumb-bar-component',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './breadcrumb-bar-component.component.html',
  styleUrl: './breadcrumb-bar-component.component.scss',
})
export class BreadcrumbBarComponentComponent {

  /** Back-compat single label.  Used when `segments` is empty. */
  @Input() label: string = 'Dashboard';

  /** Multi-segment breadcrumb.  When provided (non-empty), each segment
      is rendered with a `›` chevron between segments.  Segments with a
      non-null `link` are clickable and navigate via the `navigate` output;
      segments with `link: null` are non-clickable (typically the current page).
      Assigning this precomputes each clickable segment's `href` once (see
      {@link hrefFor}), so the template's `[href]` binding is a cheap lookup rather
      than a router serialize on every change-detection pass. */
  @Input()
  set segments(value: BreadcrumbSegment[]) {
    this._segments = value ?? [];
    this.hrefBySegment = new Map(
      this._segments
        .filter(s => s.link != null)
        .map(s => [s, this.buildHref(s)] as const),
    );
  }
  get segments(): BreadcrumbSegment[] {
    return this._segments;
  }
  private _segments: BreadcrumbSegment[] = [];
  /** Precomputed `href` per clickable segment, rebuilt only when `segments` is
      (re)assigned — the source of the cheap `hrefFor` lookup. */
  private hrefBySegment = new Map<BreadcrumbSegment, string>();

  /** Toggle the back-arrow / home-icon visibility. */
  @Input() showBack: boolean = true;
  @Input() showHome: boolean = true;

  @Output() back = new EventEmitter<void>();
  @Output() home = new EventEmitter<void>();
  /**
   * A clickable crumb was activated with a plain left-click. The host
   * ({@link AppComponent}) decides HOW to navigate — going BACK in history when the
   * crumb is already behind the current page, else a normal forward navigation — so
   * returning up the trail never leaves the just-exited page in the back stack.
   * Modifier / middle clicks are NOT emitted: the anchor keeps a real `href`, so the
   * browser handles open-in-new-tab natively (see {@link onSegmentClick}).
   */
  @Output() navigate = new EventEmitter<BreadcrumbSegment>();

  /** `Router` builds each crumb's `href` string (below) so the anchor stays a real
      link — preserving Ctrl/Cmd/middle-click "open in new tab" — while the plain-click
      navigation is delegated to the host via {@link navigate}. */
  constructor(private readonly router: Router) {}

  /** True when the consumer supplied a segments array. */
  get hasSegments(): boolean {
    return Array.isArray(this.segments) && this.segments.length > 0;
  }

  /** The crumb's URL as an `href` string (including any kept query params) so the
      anchor behaves like a normal link for modifier / middle clicks. Returns the value
      precomputed when `segments` was assigned; falls back to building it for a segment
      not in the cache. */
  hrefFor(segment: BreadcrumbSegment): string {
    return this.hrefBySegment.get(segment) ?? this.buildHref(segment);
  }

  /** Serializes a single crumb's `href`. Called once per segment when `segments` is
      assigned (see the `segments` setter), not on every change-detection pass. */
  private buildHref(segment: BreadcrumbSegment): string {
    return this.router.serializeUrl(
      this.router.createUrlTree([segment.link], { queryParams: segment.queryParams ?? undefined }),
    );
  }

  /**
   * Plain left-click on a crumb → let the host navigate (`navigate` output) and
   * suppress the anchor's default browser navigation. A modified or non-left click
   * (Ctrl/Cmd/Shift/Alt/middle) is left ALONE so the browser opens the `href` in a
   * new tab/window exactly as a normal link would.
   */
  onSegmentClick(event: MouseEvent, segment: BreadcrumbSegment): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    this.navigate.emit(segment);
  }
}
